import {z} from 'zod';
import type {
  NotificationRuleMetadata,
  NotificationVariable} from 'src/api/ProactApiClient';
import {
  ModuleEnum,
  NotificationRecipientTypeEnum,
  NotificationRuleCourseChangeTypeEnum,
  NotificationRuleTypeEnum
} from 'src/api/ProactApiClient';

// Token regex pattern to find {{VARIABLE_NAME}}
const TOKEN_REGEX = /\{\{([A-Z_0-9]+)\}\}/g;

// Field length limits (matching database constraints)
const MAX_NAME_LENGTH = 250;
const MAX_SUBJECT_LENGTH = 250;
const MAX_BODY_LENGTH = 10000;

export const extractTokensFromText = (text: string): string[] => {
  if (!text) return [];
  const matches = text.matchAll(TOKEN_REGEX);
  return [...matches].map(match => match[1]);
};

export interface NotificationRuleValidationErrors {
  name?: string;
  subject?: string;
  body?: string;
  recipients?: string;
  conditions?: string;
  formDefinitionId?: string;
  courseDefinitionId?: string;
  courseDefinitionContentInvariantId?: string;
}

export const hasChangeEventConditions = (
  metadata: NotificationRuleMetadata
): boolean => {
  return (
    (metadata.formChangeConditions?.length ?? 0) > 0 ||
    (metadata.taskAssignmentChangeConditions?.length ?? 0) > 0 ||
    (metadata.safetyEventChangeConditions?.length ?? 0) > 0 ||
    (metadata.personnelChangeConditions?.length ?? 0) > 0 ||
    (metadata.itemAssignmentChangeConditions?.length ?? 0) > 0 ||
    (metadata.courseAssignmentChangeConditions?.length ?? 0) > 0 ||
    (metadata.courseSessionChangeConditions?.length ?? 0) > 0 ||
    (metadata.courseContentChangeConditions?.length ?? 0) > 0 ||
    (metadata.attachmentChangeConditions?.length ?? 0) > 0 ||
    (metadata.inspectionChangeConditions?.length ?? 0) > 0
  );
};

export const hasConditions = (
  metadata: NotificationRuleMetadata,
  ruleType: NotificationRuleTypeEnum
): boolean => {
  if (ruleType === NotificationRuleTypeEnum.Reminder) {
    return (metadata.reminderConditions?.length ?? 0) > 0;
  }

  if (ruleType === NotificationRuleTypeEnum.ChangeEvent) {
    return hasChangeEventConditions(metadata);
  }

  return false;
};

export const hasRecipients = (metadata: NotificationRuleMetadata): boolean => {
  const hasUserGroups = (metadata.toUserGroupIds?.length ?? 0) > 0;
  const hasUserAttributes = (metadata.toUserAttributes?.length ?? 0) > 0;
  const hasFormFieldTargets = (metadata.formFieldPersonnelTargets?.length ?? 0) > 0;
  return hasUserGroups || hasUserAttributes || hasFormFieldTargets;
};

export const findInvalidTokens = (
  text: string,
  availableTokens: NotificationVariable[]
): string[] => {
  const usedTokens = extractTokensFromText(text);
  const validTokenNames = new Set(availableTokens.map(v => v.variable));
  return usedTokens.filter(token => !validTokenNames.has(token));
};

// Schema for reminder conditions
const reminderConditionSchema = z.object({
  property: z.number(),
  operator: z.number(),
  value: z.string().nullable(),
});

// Schema for form change conditions (has additional formFieldInvariantId)
const formChangeConditionSchema = z.object({
  property: z.number(),
  formFieldInvariantId: z.string().nullable().optional(),
  operator: z.number(),
  value: z.string().nullable(),
});

// Schema for generic change conditions
const changeConditionSchema = z.object({
  property: z.number(),
  operator: z.number(),
  value: z.string().nullable(),
});

// Schema for form field personnel targets
const formFieldPersonnelTargetSchema = z.object({
  formFieldInvariantId: z.string(),
  attributes: z.array(z.number()),
  recipientType: z.nativeEnum(NotificationRecipientTypeEnum),
});

// Schema is created dynamically based on available tokens.
export const createNotificationRuleSchema = (
  availableTokens: NotificationVariable[]
) => {
  return z
    .object({
      id: z.number(),
      active: z.boolean(),
      name: z.string().max(MAX_NAME_LENGTH, `Name must be ${MAX_NAME_LENGTH} characters or less`).nullish(),
      ruleType: z.number(),
      module: z.number().nullish(),
      subject: z.string().max(MAX_SUBJECT_LENGTH, `Subject must be ${MAX_SUBJECT_LENGTH} characters or less`).nullish(),
      body: z.string().max(MAX_BODY_LENGTH, `Body must be ${MAX_BODY_LENGTH} characters or less`).nullish(),
      projectId: z.number().nullish(),
      metadata: z.object({
        toUserGroupIds: z.array(z.number()).nullish(),
        toUserAttributes: z.array(z.number()).nullish(),
        module: z.number().nullish(),
        reminderConditions: z.array(reminderConditionSchema).nullish(),
        changeType: z.number().nullish(),
        formDefinitionId: z.number().nullish(),
        formChangeConditions: z.array(formChangeConditionSchema).nullish(),
        courseChangeType: z.number().nullish(),
        courseDefinitionId: z.number().nullish(),
        courseDefinitionContentInvariantId: z.string().nullish(),
        courseAssignmentChangeConditions: z.array(changeConditionSchema).nullish(),
        courseSessionChangeConditions: z.array(changeConditionSchema).nullish(),
        courseContentChangeConditions: z.array(changeConditionSchema).nullish(),
        itemAssignmentChangeConditions: z.array(changeConditionSchema).nullish(),
        taskAssignmentChangeConditions: z.array(changeConditionSchema).nullish(),
        safetyEventChangeConditions: z.array(changeConditionSchema).nullish(),
        personnelChangeConditions: z.array(changeConditionSchema).nullish(),
        attachmentChangeConditions: z.array(changeConditionSchema).nullish(),
        formFieldPersonnelTargets: z.array(formFieldPersonnelTargetSchema).nullish(),
      }),
    })
    .superRefine((data, ctx) => {
      // Validate name is required
      if (!data.name || data.name.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Name is required',
          path: ['name'],
        });
      }

      // Validate at least one recipient is selected
      if (!hasRecipients(data.metadata as NotificationRuleMetadata)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least one recipient must be selected',
          path: ['metadata', 'recipients'],
        });
      }

      // Validate at least one condition exists
      if (!hasConditions(data.metadata as NotificationRuleMetadata, data.ruleType)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least one condition is required',
          path: ['metadata', 'conditions'],
        });
      }

      // Validate subject is required
      if (!data.subject || data.subject.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Subject is required',
          path: ['subject'],
        });
      }

      // Validate body is required
      if (!data.body || data.body.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Body is required',
          path: ['body'],
        });
      }

      // Validate tokens in subject
      if (data.subject) {
        const invalidSubjectTokens = findInvalidTokens(
          data.subject,
          availableTokens
        );
        if (invalidSubjectTokens.length > 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Invalid tokens in subject: ${invalidSubjectTokens.map(t => `{{${t}}}`).join(', ')}`,
            path: ['subject'],
          });
        }
      }

      // Validate tokens in body
      if (data.body) {
        const invalidBodyTokens = findInvalidTokens(data.body, availableTokens);
        if (invalidBodyTokens.length > 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Invalid tokens in body: ${invalidBodyTokens.map(t => `{{${t}}}`).join(', ')}`,
            path: ['body'],
          });
        }
      }

      // Validate form field personnel targets
      if (data.metadata.formFieldPersonnelTargets) {
        data.metadata.formFieldPersonnelTargets.forEach((target, index) => {
          if (!target.formFieldInvariantId) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Form field is required',
              path: ['metadata', 'formFieldPersonnelTargets', index, 'formFieldInvariantId'],
            });
          }
          if (!target.attributes || target.attributes.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'At least one attribute level is required',
              path: ['metadata', 'formFieldPersonnelTargets', index, 'attributes'],
            });
          }
        });
      }

      // Module-specific validations
      const module = data.metadata.module;
      const ruleType = data.ruleType;

      // Forms module requires form selection
      if (
        module === ModuleEnum.Forms &&
        ruleType === NotificationRuleTypeEnum.ChangeEvent &&
        !data.metadata.formDefinitionId
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Form selection is required',
          path: ['metadata', 'formDefinitionId'],
        });
      }

      // Training module validations for CourseContentChange
      if (
        module === ModuleEnum.Training &&
        ruleType === NotificationRuleTypeEnum.ChangeEvent &&
        data.metadata.courseChangeType ===
          NotificationRuleCourseChangeTypeEnum.CourseContentChange
      ) {
        if (!data.metadata.courseDefinitionId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Course selection is required',
            path: ['metadata', 'courseDefinitionId'],
          });
        }
        if (!data.metadata.courseDefinitionContentInvariantId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Course content selection is required',
            path: ['metadata', 'courseDefinitionContentInvariantId'],
          });
        }
      }
    });
};

export type NotificationRuleSchemaType = z.infer<
  ReturnType<typeof createNotificationRuleSchema>
>;
